import { useEffect, useState } from 'react';
import type { FxRate } from '../../data/source';
import { useSession } from '../../state/session';
import { shortDate } from '../invoices/shared';

export const SOURCE_NAME: Record<FxRate['source'], string> = {
  base: '', manual: 'your typed rate', ecb: 'ECB', hmrc: 'HMRC', wise: 'Wise', revolut: 'Revolut',
};

export type DayRate = FxRate | null | 'none' | 'loading';

/** The day's rate, as the ledger will pick it up (typed rate for the day, the bank's feed, ECB or HMRC). */
export function useDayRate(currency: string, date: string, base: string): DayRate {
  const { source } = useSession();
  const [r, setR] = useState<DayRate>(null);
  useEffect(() => {
    if (!currency || currency === base || !date) { setR(null); return; }
    let live = true;
    setR('loading');
    source.fxRate(currency, date).then((x) => live && setR(x), () => live && setR('none'));
    return () => { live = false; };
  }, [source, currency, date, base]);
  return r;
}

export function rateHint(r: DayRate, ccy: string, base: string): string {
  const sym = base === 'EUR' ? '€' : base === 'GBP' ? '£' : `${base} `;
  if (r === 'loading' || r === null) return 'Looking up the day’s rate…';
  if (r === 'none') return `No ${ccy} rate for that day. Type one in.`;
  return `Leave blank to use ${SOURCE_NAME[r.source]} for ${shortDate(r.rate_date)}: ${sym}${r.rate} per ${ccy}`;
}

/** "1.16279070" → "1.1627907": a stored rate for editing. */
export function trimRate(v: string): string {
  return v.includes('.') ? v.replace(/0+$/, '').replace(/\.$/, '') : v;
}

export const rateOk = (v: string) => !v.trim() || (/^\d+(\.\d{1,8})?$/.test(v.trim()) && Number(v) > 0);

/** The rate that turns a foreign total into the amount the bank paid: 8 places, as the ledger stores it. */
export function rateFromPaid(paid: string, gross: string): string | null {
  const p = Number(paid), g = Number(gross);
  if (!(p > 0) || !(g > 0)) return null;
  return trimRate((p / g).toFixed(8));
}
